import { useQuery, useQueryClient } from '@tanstack/react-query';
import { keyBy, startCase, uniqBy } from 'es-toolkit/compat';
import { useCallback, useMemo } from 'react';

import { Permission } from 'interfaces/permissionOption';
import { InternalUser, UserCredentials } from 'interfaces/userCredentials';
import { canQueryUsersEndpoint } from 'utils/canQueryUsersEndpoint';
import { getAllUserCredentials } from 'utils/queryHooks/usePaginatedUserCredentials';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';

export const USER_DETAILS_STALE_TIME = 5 * 60 * 1000;

export const useUsersById = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { labId } = useCurrentLabId();
  const queryClient = useQueryClient();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const canListLabUsers = canQueryUsersEndpoint(hasPermission);
  const canListNucleaiUsers =
    canListLabUsers &&
    hasPermission(Permission.ApplyPermissionsAcrossLabs) &&
    hasPermission(Permission.ManageNucleaiUsers);

  const {
    data: users,
    isLoading: isLoadingUsers,
    isError: isUsersError,
  } = useQuery<UserCredentials[]>(
    ['usersById', labId, canListNucleaiUsers],
    async () => {
      if (canListNucleaiUsers) {
        const [internalUsers, labUsers] = await Promise.all([
          getAllUserCredentials({ labId, listNucleaiUsers: true, queryClient }),
          getAllUserCredentials({ labId, listNucleaiUsers: false, queryClient }),
        ]);
        return uniqBy([...internalUsers, ...labUsers], 'id');
      }

      return getAllUserCredentials({ labId, listNucleaiUsers: false, queryClient });
    },
    {
      enabled: Boolean(labId) && enabled && canListLabUsers && !isLoadingPermissions,
      staleTime: USER_DETAILS_STALE_TIME,
    }
  );

  const usersById = useMemo<Record<string, UserCredentials>>(
    // Create a mapping of userId to user object, accounting for cases where userId might be stored with or without 'auth0|' prefix
    () => ({
      ...keyBy(users, (user) => user.auth0UserId),
      ...keyBy(users, (user) => user.auth0UserId?.replace(/^auth0\|/, '')),
      ...keyBy(users, (user) => user.id),
    }),
    [users]
  );

  const getUserDetailsById = useCallback(
    (userId?: string | null): InternalUser | undefined => {
      if (!userId) return undefined;
      const user = usersById[userId] || usersById[userId.replace(/^auth0\|/, '')];
      return user ? { id: user.id, name: user.name, primaryEmail: user.primaryEmail } : undefined;
    },
    [usersById]
  );

  const getUserNameById = useCallback(
    (userId?: string): string | undefined => {
      if (!userId) {
        return undefined;
      }

      const user = getUserDetailsById(userId);
      const nameFromUserObject = user ? (user?.name ? startCase(user?.name) : user?.primaryEmail) : undefined;
      return nameFromUserObject || userId;
    },
    [getUserDetailsById]
  );

  return {
    usersById,
    getUserDetailsById,
    getUserNameById,
    isLoadingUsers,
    isUsersError,
    canLoadUserDetails: Boolean(labId) && enabled && canListLabUsers && !isLoadingPermissions,
  };
};

export default useUsersById;
